import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  getOrganizationSettings, updateOrganizationSettings, toggleInspectionMode,
  getNotificationPreferences, updateNotificationPreferences, resetNotificationPreferences
} from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Settings, Globe, Building2, Shield, Calendar, Bell, Mail, 
  AlertTriangle, FileText, ClipboardCheck, Users, MessageSquare,
  RefreshCw, Clock, Plane, Save, FileDown, Loader2, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import EmailSettingsPanel from '@/components/EmailSettingsPanel';

const OPERATOR_TYPES = [
  { value: 'commercial_airline', label: 'Commercial Airline' },
  { value: 'mro', label: 'MRO / Maintenance Provider' },
  { value: 'ground_handler', label: 'Ground Handling' },
  { value: 'corporate', label: 'Corporate / Business Aviation' },
  { value: 'training', label: 'Training Organisation (ATO)' },
  { value: 'airport_operator', label: 'Airport Operator' },
  { value: 'other', label: 'Other' },
];

const REGULATORS = [
  { value: 'GACA', label: 'GACA (Saudi Arabia)' },
  { value: 'EASA', label: 'EASA (Europe)' },
  { value: 'FAA', label: 'FAA (USA)' },
  { value: 'CAAS', label: 'CAAS (Singapore)' },
  { value: 'CASA', label: 'CASA (Australia)' },
  { value: 'DGCA', label: 'DGCA (India)' },
  { value: 'ICAO', label: 'ICAO (Reference)' },
  { value: 'other', label: 'Other' },
];

const NOTIFICATION_CHANNELS = [
  { value: 'both', label: 'Email & In-App', icon: '📧🔔' },
  { value: 'email', label: 'Email Only', icon: '📧' },
  { value: 'in_app', label: 'In-App Only', icon: '🔔' },
  { value: 'none', label: 'Disabled', icon: '🚫' },
];

const NotificationRow = ({ label, description, value, onChange, icon: Icon }) => (
  <div className="flex items-center justify-between py-3 border-b border-slate-800 last:border-0">
    <div className="flex items-start gap-3">
      {Icon && <Icon className="w-4 h-4 text-slate-500 mt-1" />}
      <div>
        <Label className="text-slate-300">{label}</Label>
        {description && <p className="text-slate-500 text-xs mt-0.5">{description}</p>}
      </div>
    </div>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[160px] bg-slate-950 border-slate-800">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-slate-900 border-slate-800">
        {NOTIFICATION_CHANNELS.map(ch => (
          <SelectItem key={ch.value} value={ch.value}>
            <span className="flex items-center gap-2">
              <span className="text-xs">{ch.icon}</span>
              {ch.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

export default function SettingsPage() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const { language, changeLanguage } = useLanguage();
  const [settings, setSettings] = useState(null);
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inspectionHours, setInspectionHours] = useState(24);
  const [activeTab, setActiveTab] = useState('general');
  // Company Profile (admin-only) — separate from Organization Settings.
  // Uses /api/company/profile (identity record captured by onboarding wizard).
  const [companyProfile, setCompanyProfile] = useState(null);
  const [profileDirty, setProfileDirty] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [downloadingKickoff, setDownloadingKickoff] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const promises = [
        getOrganizationSettings(api),
        getNotificationPreferences(api),
      ];
      if (user?.role === 'admin') {
        promises.push(api.get('/company/profile'));
      }
      const results = await Promise.all(promises);
      setSettings(results[0].data);
      setNotifPrefs(results[1].data);
      if (user?.role === 'admin') {
        setCompanyProfile(results[2].data || {});
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const setProfileField = (key, value) => {
    setCompanyProfile((prev) => ({ ...(prev || {}), [key]: value }));
    setProfileDirty(true);
  };

  const saveCompanyProfile = async () => {
    if (!companyProfile) return;
    if (!(companyProfile.name || '').trim()) {
      toast.error('Company name is required.');
      return;
    }
    setProfileSaving(true);
    try {
      const payload = {
        name: companyProfile.name,
        legal_name: companyProfile.legal_name,
        operator_type: companyProfile.operator_type,
        country: companyProfile.country,
        regulator: companyProfile.regulator,
        base_airport_icao: companyProfile.base_airport_icao,
        base_airport_name: companyProfile.base_airport_name,
        timezone: companyProfile.timezone,
        contact_email: companyProfile.contact_email,
        contact_phone: companyProfile.contact_phone,
        website: companyProfile.website,
        logo_url: companyProfile.logo_url,
        aoc_number: companyProfile.aoc_number,
      };
      const { data } = await api.put('/company/profile', payload);
      setCompanyProfile(data || companyProfile);
      setProfileDirty(false);
      toast.success('Company profile updated');
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to save company profile';
      toast.error(msg);
    } finally {
      setProfileSaving(false);
    }
  };

  const downloadKickoff = async () => {
    setDownloadingKickoff(true);
    try {
      const res = await api.get('/company/kickoff-pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'AeroGuard_Day1_Setup.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingKickoff(false);
    }
  };

  const handleUpdateSetting = async (key, value) => {
    setSaving(true);
    try { await updateOrganizationSettings(api, { [key]: value }); setSettings({ ...settings, [key]: value }); }
    catch (error) { console.error('Failed to update setting:', error); }
    finally { setSaving(false); }
  };

  const handleToggleInspection = async (enabled) => {
    setSaving(true);
    try {
      const response = await toggleInspectionMode(api, { enabled, hours_duration: inspectionHours });
      setSettings({ ...settings, inspection_mode_enabled: enabled, inspection_mode_expires: response.data.expires });
    } catch (error) { console.error('Failed to toggle inspection mode:', error); }
    finally { setSaving(false); }
  };

  const handleNotifPrefChange = async (key, value) => {
    setSaving(true);
    try {
      await updateNotificationPreferences(api, { [key]: value });
      setNotifPrefs({ ...notifPrefs, [key]: value });
      toast.success('Notification preference updated');
    } catch (error) {
      console.error('Failed to update notification preference:', error);
      toast.error('Failed to update preference');
    } finally {
      setSaving(false);
    }
  };

  const handleResetNotifPrefs = async () => {
    if (!confirm('Reset all notification preferences to defaults?')) return;
    setSaving(true);
    try {
      const response = await resetNotificationPreferences(api);
      setNotifPrefs(response.data.preferences);
      toast.success('Notification preferences reset to defaults');
    } catch (error) {
      console.error('Failed to reset preferences:', error);
      toast.error('Failed to reset preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="spinner w-8 h-8" /></div>;

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-bold text-white font-barlow tracking-tight" data-testid="settings-title">{t('settings.title')}</h1>
        <p className="text-slate-400 mt-1">Configure system settings and preferences</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50">
          <TabsTrigger value="general" className="data-[state=active]:bg-slate-700">
            <Settings className="w-4 h-4 mr-2" />General
          </TabsTrigger>
          {user?.role === 'admin' && (
            <TabsTrigger value="company" data-testid="settings-tab-company" className="data-[state=active]:bg-slate-700">
              <Plane className="w-4 h-4 mr-2" />Company Profile
            </TabsTrigger>
          )}
          {user?.role === 'admin' && (
            <TabsTrigger value="email" data-testid="settings-tab-email" className="data-[state=active]:bg-slate-700">
              <Mail className="w-4 h-4 mr-2" />Email
            </TabsTrigger>
          )}
          <TabsTrigger value="notifications" className="data-[state=active]:bg-slate-700">
            <Bell className="w-4 h-4 mr-2" />Notifications
          </TabsTrigger>
        </TabsList>

        {/* General Settings Tab */}
        <TabsContent value="general" className="space-y-6 mt-6">
          {/* Language Settings */}
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><Globe className="w-5 h-5 text-sky-400" />{t('settings.language')}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div><Label className="text-slate-300">{t('settings.language')}</Label><p className="text-slate-500 text-sm">Select your preferred language</p></div>
                <Select value={language} onValueChange={changeLanguage}>
                  <SelectTrigger className="w-[180px] bg-slate-950 border-slate-800" data-testid="language-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="ar">العربية (Arabic)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Organization Settings */}
          {user?.role === 'admin' && (
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader><CardTitle className="text-white flex items-center gap-2"><Building2 className="w-5 h-5 text-violet-400" />{t('settings.organization')}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-slate-300">Organization Name</Label>
                    <Input value={settings?.name || ''} onChange={(e) => setSettings({...settings, name: e.target.value})} onBlur={(e) => handleUpdateSetting('name', e.target.value)} className="bg-slate-950 border-slate-800 mt-1" data-testid="org-name-input" />
                  </div>
                  <div><Label className="text-slate-300">Organization Name (Arabic)</Label>
                    <Input value={settings?.name_ar || ''} onChange={(e) => setSettings({...settings, name_ar: e.target.value})} onBlur={(e) => handleUpdateSetting('name_ar', e.target.value)} className="bg-slate-950 border-slate-800 mt-1" dir="rtl" data-testid="org-name-ar-input" />
                  </div>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div><Label className="text-slate-300">Hijri Calendar</Label><p className="text-slate-500 text-sm">Enable Hijri calendar alongside Gregorian</p></div>
                  <Switch checked={settings?.hijri_calendar_enabled} onCheckedChange={(checked) => handleUpdateSetting('hijri_calendar_enabled', checked)} data-testid="hijri-toggle" />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Inspection Mode */}
          {['admin', 'safety_manager'].includes(user?.role) && (
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader><CardTitle className="text-white flex items-center gap-2"><Shield className="w-5 h-5 text-emerald-400" />{t('settings.inspectionMode')}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-400 text-sm">Enable read-only access for regulatory inspectors. This creates a time-limited portal for GACA/EASA inspectors to view safety data.</p>
                <div className="flex items-center gap-4">
                  <div><Label className="text-slate-300">{t('settings.inspectionDuration')}</Label>
                    <Input type="number" value={inspectionHours} onChange={(e) => setInspectionHours(parseInt(e.target.value))} className="w-24 bg-slate-950 border-slate-800 mt-1" />
                  </div>
                  <div className="pt-6">
                    <Button onClick={() => handleToggleInspection(!settings?.inspection_mode_enabled)} className={settings?.inspection_mode_enabled ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'} disabled={saving} data-testid="inspection-toggle-btn">
                      {settings?.inspection_mode_enabled ? 'Disable Inspection Mode' : t('settings.enableInspection')}
                    </Button>
                  </div>
                </div>
                {settings?.inspection_mode_enabled && settings?.inspection_mode_expires && (
                  <p className="text-emerald-400 text-sm flex items-center gap-2"><Calendar className="w-4 h-4" />Inspection mode active until: {new Date(settings.inspection_mode_expires).toLocaleString()}</p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Company Profile Tab (admin-only) — identity record from onboarding wizard */}
        {user?.role === 'admin' && (
          <TabsContent value="company" className="space-y-6 mt-6" data-testid="company-profile-tab">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Plane className="w-5 h-5 text-cyan-400" />
                      Company Profile
                      {companyProfile?.completed && (
                        <Badge variant="outline" className="border-emerald-600/40 text-emerald-300 text-xs ml-2">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Onboarding complete
                        </Badge>
                      )}
                    </CardTitle>
                    <p className="text-slate-400 text-sm mt-1">
                      Identity record captured during onboarding — appears on the Day-1 report, exports,
                      and regulatory submissions.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={downloadKickoff}
                      disabled={downloadingKickoff}
                      variant="outline"
                      className="border-slate-700 text-slate-200 hover:bg-slate-800"
                      data-testid="settings-download-kickoff-pdf-btn"
                    >
                      {downloadingKickoff
                        ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing…</>
                        : <><FileDown className="w-4 h-4 mr-2" /> Day-1 PDF</>}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Basics */}
                <section className="space-y-3">
                  <h3 className="text-slate-200 text-sm font-semibold uppercase tracking-wide">Company basics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Company name *</Label>
                      <Input
                        value={companyProfile?.name || ''}
                        onChange={(e) => setProfileField('name', e.target.value)}
                        placeholder="e.g. Saudi Aviation Group"
                        className="bg-slate-950 border-slate-800 mt-1"
                        data-testid="cp-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Legal name</Label>
                      <Input
                        value={companyProfile?.legal_name || ''}
                        onChange={(e) => setProfileField('legal_name', e.target.value)}
                        placeholder="Legal / registered name (optional)"
                        className="bg-slate-950 border-slate-800 mt-1"
                        data-testid="cp-legal-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Operator type</Label>
                      <Select
                        value={companyProfile?.operator_type || ''}
                        onValueChange={(v) => setProfileField('operator_type', v)}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-800 mt-1" data-testid="cp-operator-type-select">
                          <SelectValue placeholder="Select operator type" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800">
                          {OPERATOR_TYPES.map(o => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300">Country (ISO-2)</Label>
                      <Input
                        value={companyProfile?.country || ''}
                        onChange={(e) => setProfileField('country', e.target.value.toUpperCase().slice(0, 2))}
                        placeholder="e.g. SA"
                        className="bg-slate-950 border-slate-800 mt-1"
                        maxLength={2}
                        data-testid="cp-country-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Regulator</Label>
                      <Select
                        value={companyProfile?.regulator || ''}
                        onValueChange={(v) => setProfileField('regulator', v)}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-800 mt-1" data-testid="cp-regulator-select">
                          <SelectValue placeholder="Select regulator" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800">
                          {REGULATORS.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300">AOC number</Label>
                      <Input
                        value={companyProfile?.aoc_number || ''}
                        onChange={(e) => setProfileField('aoc_number', e.target.value)}
                        placeholder="Air Operator Certificate (optional)"
                        className="bg-slate-950 border-slate-800 mt-1"
                        data-testid="cp-aoc-input"
                      />
                    </div>
                  </div>
                </section>

                {/* Base of operations */}
                <section className="space-y-3 pt-4 border-t border-slate-800">
                  <h3 className="text-slate-200 text-sm font-semibold uppercase tracking-wide">Base of operations</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Base airport ICAO</Label>
                      <Input
                        value={companyProfile?.base_airport_icao || ''}
                        onChange={(e) => setProfileField('base_airport_icao', e.target.value.toUpperCase().slice(0, 4))}
                        placeholder="e.g. OERK"
                        className="bg-slate-950 border-slate-800 mt-1 font-mono"
                        maxLength={4}
                        data-testid="cp-icao-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Base airport name</Label>
                      <Input
                        value={companyProfile?.base_airport_name || ''}
                        onChange={(e) => setProfileField('base_airport_name', e.target.value)}
                        placeholder="e.g. King Khalid International"
                        className="bg-slate-950 border-slate-800 mt-1"
                        data-testid="cp-airport-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Timezone</Label>
                      <Input
                        value={companyProfile?.timezone || ''}
                        onChange={(e) => setProfileField('timezone', e.target.value)}
                        placeholder="e.g. Asia/Riyadh"
                        className="bg-slate-950 border-slate-800 mt-1 font-mono"
                        data-testid="cp-timezone-input"
                      />
                    </div>
                  </div>
                </section>

                {/* Contact & branding */}
                <section className="space-y-3 pt-4 border-t border-slate-800">
                  <h3 className="text-slate-200 text-sm font-semibold uppercase tracking-wide">Contact &amp; branding</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Contact email</Label>
                      <Input
                        type="email"
                        value={companyProfile?.contact_email || ''}
                        onChange={(e) => setProfileField('contact_email', e.target.value)}
                        placeholder="safety@yourcompany.sa"
                        className="bg-slate-950 border-slate-800 mt-1"
                        data-testid="cp-email-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Contact phone</Label>
                      <Input
                        value={companyProfile?.contact_phone || ''}
                        onChange={(e) => setProfileField('contact_phone', e.target.value)}
                        placeholder="+966 ..."
                        className="bg-slate-950 border-slate-800 mt-1"
                        data-testid="cp-phone-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Website</Label>
                      <Input
                        value={companyProfile?.website || ''}
                        onChange={(e) => setProfileField('website', e.target.value)}
                        placeholder="https://yourcompany.sa"
                        className="bg-slate-950 border-slate-800 mt-1"
                        data-testid="cp-website-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Logo URL</Label>
                      <Input
                        value={companyProfile?.logo_url || ''}
                        onChange={(e) => setProfileField('logo_url', e.target.value)}
                        placeholder="Public HTTPS image URL"
                        className="bg-slate-950 border-slate-800 mt-1"
                        data-testid="cp-logo-input"
                      />
                    </div>
                  </div>
                </section>

                {/* Actions */}
                <div className="pt-4 border-t border-slate-800 flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-slate-500 text-xs">
                    {profileDirty
                      ? 'You have unsaved changes.'
                      : 'All changes saved.'}
                  </p>
                  <Button
                    onClick={saveCompanyProfile}
                    disabled={profileSaving || !profileDirty}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white"
                    data-testid="cp-save-btn"
                  >
                    {profileSaving
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                      : <><Save className="w-4 h-4 mr-2" /> Save Company Profile</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Email Provider Tab (admin-only) */}
        {user?.role === 'admin' && (
          <TabsContent value="email" className="mt-6" data-testid="email-settings-tab">
            <EmailSettingsPanel />
          </TabsContent>
        )}

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-6 mt-6">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Bell className="w-5 h-5 text-cyan-400" />Notification Preferences
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleResetNotifPrefs}
                  className="border-slate-700 text-slate-300"
                  disabled={saving}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />Reset to Defaults
                </Button>
              </div>
              <p className="text-slate-500 text-sm mt-1">Control how and when you receive notifications</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Incident Notifications */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-orange-400" />
                  <h3 className="text-white font-medium">Incidents & Hazards</h3>
                </div>
                <div className="bg-slate-800/30 rounded-lg p-4">
                  <NotificationRow
                    label="Assigned to Me"
                    description="When an incident is assigned to you"
                    value={notifPrefs?.incident_assigned || 'both'}
                    onChange={(v) => handleNotifPrefChange('incident_assigned', v)}
                  />
                  <NotificationRow
                    label="Status Changes"
                    description="When status of your incidents changes"
                    value={notifPrefs?.incident_status_change || 'in_app'}
                    onChange={(v) => handleNotifPrefChange('incident_status_change', v)}
                  />
                  <NotificationRow
                    label="@Mentions"
                    description="When someone mentions you in a comment"
                    value={notifPrefs?.incident_mentioned || 'both'}
                    onChange={(v) => handleNotifPrefChange('incident_mentioned', v)}
                  />
                </div>
              </div>

              {/* CAPA Notifications */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <ClipboardCheck className="w-4 h-4 text-emerald-400" />
                  <h3 className="text-white font-medium">CAPA (Corrective Actions)</h3>
                </div>
                <div className="bg-slate-800/30 rounded-lg p-4">
                  <NotificationRow
                    label="Assigned to Me"
                    description="When a CAPA is assigned to you"
                    value={notifPrefs?.capa_assigned || 'both'}
                    onChange={(v) => handleNotifPrefChange('capa_assigned', v)}
                  />
                  <NotificationRow
                    label="Due Soon"
                    description="Reminder when CAPA is approaching due date"
                    value={notifPrefs?.capa_due_soon || 'both'}
                    onChange={(v) => handleNotifPrefChange('capa_due_soon', v)}
                  />
                  <NotificationRow
                    label="Overdue Alert"
                    description="Alert when CAPA becomes overdue"
                    value={notifPrefs?.capa_overdue || 'both'}
                    onChange={(v) => handleNotifPrefChange('capa_overdue', v)}
                  />
                </div>
              </div>

              {/* Risk Notifications */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-4 h-4 text-red-400" />
                  <h3 className="text-white font-medium">Risk Management</h3>
                </div>
                <div className="bg-slate-800/30 rounded-lg p-4">
                  <NotificationRow
                    label="Assigned to Me"
                    description="When a risk is assigned to you"
                    value={notifPrefs?.risk_assigned || 'both'}
                    onChange={(v) => handleNotifPrefChange('risk_assigned', v)}
                  />
                  <NotificationRow
                    label="Risk Level Change"
                    description="When risk level changes (e.g., medium to high)"
                    value={notifPrefs?.risk_level_change || 'both'}
                    onChange={(v) => handleNotifPrefChange('risk_level_change', v)}
                  />
                </div>
              </div>

              {/* Collaboration Notifications */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="w-4 h-4 text-violet-400" />
                  <h3 className="text-white font-medium">Collaboration</h3>
                </div>
                <div className="bg-slate-800/30 rounded-lg p-4">
                  <NotificationRow
                    label="@Mentions"
                    description="When someone @mentions you"
                    value={notifPrefs?.collab_mentions || 'both'}
                    onChange={(v) => handleNotifPrefChange('collab_mentions', v)}
                  />
                  <NotificationRow
                    label="Comments"
                    description="New comments on items you're involved with"
                    value={notifPrefs?.collab_comments || 'in_app'}
                    onChange={(v) => handleNotifPrefChange('collab_comments', v)}
                  />
                  <NotificationRow
                    label="Evidence Pins"
                    description="When evidence is pinned to your items"
                    value={notifPrefs?.collab_evidence_pins || 'in_app'}
                    onChange={(v) => handleNotifPrefChange('collab_evidence_pins', v)}
                  />
                </div>
              </div>

              {/* Meeting Notifications */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-blue-400" />
                  <h3 className="text-white font-medium">Meetings</h3>
                </div>
                <div className="bg-slate-800/30 rounded-lg p-4">
                  <NotificationRow
                    label="Meeting Invites"
                    description="When invited to safety meetings"
                    value={notifPrefs?.meeting_invite || 'both'}
                    onChange={(v) => handleNotifPrefChange('meeting_invite', v)}
                  />
                  <NotificationRow
                    label="Meeting Reminders"
                    description="Reminder before scheduled meetings"
                    value={notifPrefs?.meeting_reminder || 'email'}
                    onChange={(v) => handleNotifPrefChange('meeting_reminder', v)}
                  />
                  <NotificationRow
                    label="Action Items"
                    description="When assigned meeting action items"
                    value={notifPrefs?.meeting_action_assigned || 'both'}
                    onChange={(v) => handleNotifPrefChange('meeting_action_assigned', v)}
                  />
                </div>
              </div>

              {/* Policy Notifications */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4 text-cyan-400" />
                  <h3 className="text-white font-medium">Policies</h3>
                </div>
                <div className="bg-slate-800/30 rounded-lg p-4">
                  <NotificationRow
                    label="Pending Approval"
                    description="When policies require your approval"
                    value={notifPrefs?.policy_pending_approval || 'email'}
                    onChange={(v) => handleNotifPrefChange('policy_pending_approval', v)}
                  />
                  <NotificationRow
                    label="Policy Updates"
                    description="When policies are updated"
                    value={notifPrefs?.policy_updated || 'in_app'}
                    onChange={(v) => handleNotifPrefChange('policy_updated', v)}
                  />
                </div>
              </div>

              {/* Email Digest */}
              <div className="pt-4 border-t border-slate-800">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="w-4 h-4 text-yellow-400" />
                  <h3 className="text-white font-medium">Email Digest</h3>
                </div>
                <div className="bg-slate-800/30 rounded-lg p-4">
                  <div className="flex items-center justify-between py-3 border-b border-slate-800">
                    <div>
                      <Label className="text-slate-300">Enable Email Digest</Label>
                      <p className="text-slate-500 text-xs mt-0.5">Receive a summary of notifications instead of individual emails</p>
                    </div>
                    <Switch
                      checked={notifPrefs?.email_digest_enabled}
                      onCheckedChange={(checked) => handleNotifPrefChange('email_digest_enabled', checked)}
                    />
                  </div>
                  {notifPrefs?.email_digest_enabled && (
                    <div className="flex items-center justify-between py-3">
                      <div>
                        <Label className="text-slate-300">Digest Frequency</Label>
                        <p className="text-slate-500 text-xs mt-0.5">How often to send the digest</p>
                      </div>
                      <Select 
                        value={notifPrefs?.email_digest_frequency || 'daily'} 
                        onValueChange={(v) => handleNotifPrefChange('email_digest_frequency', v)}
                      >
                        <SelectTrigger className="w-[140px] bg-slate-950 border-slate-800">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800">
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>

              {/* Quiet Hours */}
              <div className="pt-4 border-t border-slate-800">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <h3 className="text-white font-medium">Quiet Hours</h3>
                  <Badge variant="outline" className="border-slate-600 text-slate-400 text-xs">Coming Soon</Badge>
                </div>
                <div className="bg-slate-800/30 rounded-lg p-4 opacity-50">
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <Label className="text-slate-300">Enable Quiet Hours</Label>
                      <p className="text-slate-500 text-xs mt-0.5">Pause non-urgent notifications during specified hours</p>
                    </div>
                    <Switch disabled />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
